/**
 * Canonical workspace import orchestration.
 * Contract: docs/doar-core/DOARSPACE_UNIVERSAL_WORKSPACE_CONTRACT.md #6, #10.
 *
 * Turns a user's file selection into the EXACT backend Asset it becomes, and
 * nothing else. It deliberately does not navigate, activate, switch dimensions,
 * create DoARs or projects, or touch ProjectContext: the canonical entry
 * handoff (WorkspaceEntryCoordinator) already owns all of that, and an import
 * that also decided where to open would be a second entry owner.
 *
 * Transport is NOT reimplemented here - utils/uploadClient.js remains the only
 * owner of presigning, S3 PUT, multipart and package registration. File
 * capability is NOT redefined here either - utils/fileTypeMap.js remains the
 * only authority on what may be stored, and representationResolver decides what
 * the stored Asset opens in afterwards.
 */
import { uploadFile, uploadPackage, attachImportedSource } from "../../../utils/uploadClient";
import { getCapability, getFileInfoFromName } from "../../../utils/fileTypeMap";
import { IMPORT_PLAN_KIND, planWorkspaceImport } from "./modelPackagePlan";
import { validateModelPackage } from './validateModelPackage';
import {admitWorkspaceBatch} from '../../../api/importBatchApi';
import {currentWorkspaceAccountScope} from '../workspaceAccountScope';
import {studioRequestError} from '../../../api/studioRequestError';

export const IMPORT_ERROR = Object.freeze({
  NO_FILES: "no_files",
  MULTIPLE_ROOTS: "multiple_roots",
  ORPHAN_COMPANIONS: "orphan_companions",
  UNSUPPORTED_FILE: "unsupported_file",
  NO_PROJECT: "no_project",
  UPLOAD_FAILED: "upload_failed",
  /** The transport resolved, but what came back is not an openable import. */
  INVALID_UPLOAD_RESULT: "invalid_upload_result",
  ADMISSION_FAILED: 'admission_failed',
  INVALID_PACKAGE: 'invalid_package',
  PACKAGE_INCOMPLETE: 'package_incomplete',
});

export const IMPORT_ERROR_MESSAGE = Object.freeze({
  [IMPORT_ERROR.NO_FILES]: "Choose a file to import.",
  [IMPORT_ERROR.MULTIPLE_ROOTS]: "Import one item or one model package at a time.",
  [IMPORT_ERROR.ORPHAN_COMPANIONS]: "These files belong to a 3D model. Include the model file itself (for example the .obj or .gltf).",
  [IMPORT_ERROR.UNSUPPORTED_FILE]: "This file type cannot be stored in DoARSpace yet.",
  [IMPORT_ERROR.NO_PROJECT]: "Your Space could not be resolved, so nothing was uploaded.",
  [IMPORT_ERROR.UPLOAD_FAILED]: "This file could not be uploaded.",
  [IMPORT_ERROR.INVALID_UPLOAD_RESULT]: "The upload returned an unavailable or different Asset. Check your Space before uploading again; a source may already be stored.",
  [IMPORT_ERROR.ADMISSION_FAILED]: 'Your authenticated account or exact destination project could not be verified. Nothing was uploaded.',
  [IMPORT_ERROR.INVALID_PACKAGE]: 'Choose a valid complete model package.',
  [IMPORT_ERROR.PACKAGE_INCOMPLETE]: 'The source is stored, but its package is incomplete. Repair the stored Asset or check missing companions before uploading again.',
});

/**
 * The only Asset lifecycles a completed root import may hand off.
 *   ready       - a durable representation is available;
 *   processing  - the source is stored durably, a derivative is still running;
 *   unsupported - a durable SOURCE_ONLY source is stored.
 * Anything else (failed, still uploading, provisional, absent or unrecognised)
 * is not an openable import, however the transport resolved. reference_only is
 * deliberately excluded: that lifecycle belongs to the existing remote/reference
 * compatibility flows, never to /uploads/complete.
 */
const HANDOFF_READY_STATUSES = Object.freeze(new Set(["ready", "processing", "unsupported"]));

/**
 * Whether a completed upload actually produced something openable.
 * Exported for focused tests; the service applies it itself.
 */
export function isHandoffableAsset(asset, fileInfo = null) {
  if (!asset?._id) return false;
  const status = typeof asset.status === "string" ? asset.status : "";
  if (!HANDOFF_READY_STATUSES.has(status)) return false;
  // "unsupported" is the stored-source lifecycle. Accept it only for a format
  // the shared registry really classifies as SOURCE_ONLY - never on the status
  // string alone, and never via a private capability table here.
  if (status === "unsupported") {
    const capability = fileInfo?.capability || getCapability(fileInfo?.ext);
    return capability === "SOURCE_ONLY";
  }
  return true;
}

export class WorkspaceImportError extends Error {
  constructor(code, cause = null) {
    super(IMPORT_ERROR_MESSAGE[code] || IMPORT_ERROR_MESSAGE[IMPORT_ERROR.UPLOAD_FAILED]);
    this.name = "WorkspaceImportError";
    this.code = code;
    this.cause = cause;
    this.storedAsset = cause?.storedAsset || null;
  }
}

/**
 * Structural + capability validation for ONE logical import, with no upload.
 * Separated so a caller can reject a selection before any bytes move.
 *
 * @returns {{ ok: boolean, code: string|null, rootFile: File|null, companions: File[], fileInfo: object|null }}
 */
export function validateWorkspaceImport(files) {
  const plan = planWorkspaceImport(files);

  if (plan.kind === IMPORT_PLAN_KIND.EMPTY) {
    return { ok: false, code: IMPORT_ERROR.NO_FILES, rootFile: null, companions: [], fileInfo: null };
  }
  if (plan.kind === IMPORT_PLAN_KIND.MULTIPLE_ROOTS) {
    return { ok: false, code: IMPORT_ERROR.MULTIPLE_ROOTS, rootFile: null, companions: [], fileInfo: null };
  }
  if (plan.kind === IMPORT_PLAN_KIND.ORPHAN_COMPANIONS) {
    return { ok: false, code: IMPORT_ERROR.ORPHAN_COMPANIONS, rootFile: null, companions: plan.companions, fileInfo: null };
  }

  // Capability truth comes only from the shared registry.
  const fileInfo = getFileInfoFromName(plan.rootFile?.name);
  const capability = getCapability(fileInfo?.ext);
  if (!fileInfo || capability === "REJECTED") {
    return { ok: false, code: IMPORT_ERROR.UNSUPPORTED_FILE, rootFile: plan.rootFile, companions: plan.companions, fileInfo: null };
  }

  return { ok: true, code: null, rootFile: plan.rootFile, companions: plan.companions, fileInfo };
}

/**
 * Uploads ONE logical import into an existing project and returns the exact
 * backend Asset. A SOURCE_ONLY or still-processing Asset is a SUCCESSFUL
 * import: the durable source is stored, and the workspace shows that truth.
 *
 * @param {object} input
 * @param {File[]|FileList} input.files
 * @param {string} input.projectId - the already-resolved destination project.
 * @param {import('axios').AxiosInstance} input.api
 * @param {(pct: number, phase: string) => void} [input.onProgress]
 * @param {AbortSignal} [input.signal] - cancels an ordinary single-file upload.
 *   Model packages are deliberately NOT given a signal: uploadClient cannot
 *   currently abort companion uploads atomically, and pretending otherwise would
 *   be a false cancellation guarantee.
 * @returns {Promise<{ asset: object, packageInfo: { isPackage: boolean, companionCount: number }, fileInfo: object }>}
 */
export async function importWorkspaceFiles({ files, projectId, api, onProgress, signal, intake, admit=false, onRevisionSaved } = {}) {
  const scope=currentWorkspaceAccountScope();
  if(admit){const transport=api;const changed=()=>Object.assign(Error('Account or authenticated session changed during import. Reopen the project in the current account before retrying.'),{code:'IMPORT_SESSION_CHANGED'});api=Object.fromEntries(['get','post'].map(method=>[method,async(...args)=>{if(scope!==currentWorkspaceAccountScope())throw changed();const response=await transport[method](...args);if(scope!==currentWorkspaceAccountScope())throw changed();return response;}]));}
  const validation = validateWorkspaceImport(files);
  if (!validation.ok) throw new WorkspaceImportError(validation.code);
  if (!projectId) throw new WorkspaceImportError(IMPORT_ERROR.NO_PROJECT);

  const { rootFile, companions, fileInfo } = validation;
  try { await validateModelPackage(rootFile, companions); } catch (cause) { const failure = new WorkspaceImportError(IMPORT_ERROR.INVALID_PACKAGE, cause); failure.message = cause.message; throw failure; }
  let userId, stage = 'Import account verification';
  const invalidVerification = (message, response) => Object.assign(new Error(message), { code: 'IMPORT_VERIFICATION_INVALID', response });
  try {
    const auth = await api.get('/auth/me', { timeout: 15000, signal });
    const user = auth?.data?.user, ids = [user?.id, user?._id].filter(value => value !== undefined);
    // The public auth route serializes user.id; legacy adapters use _id.
    // Neither a missing profile nor conflicting identities may authorize bytes.
    if (!ids.length || ids.some(id => typeof id !== 'string' || !/^[a-f0-9]{24}$/i.test(id)) || new Set(ids.map(id => id.toLowerCase())).size !== 1) {
      throw invalidVerification(`The account service responded${auth?.status ? ` with HTTP ${auth.status}` : ''}, but did not return a valid, consistent account identity. Sign in again before retrying.`, auth);
    }
    userId = ids[0].toLowerCase();
    stage = 'Import destination verification';
    const response = await api.get('/project/' + projectId, { timeout: 15000, signal }), destination = response?.data?.project;
    if (String(destination?._id) !== String(projectId) || String(destination?.userId) !== userId || destination.deleted) throw invalidVerification('The destination response does not identify an available project owned by the verified account. Reopen this same project before retrying.', response);
    if (signal?.aborted) throw Object.assign(new Error('Upload cancelled'), {code:'ERR_CANCELED'});
  } catch (cause) { const error = new WorkspaceImportError(IMPORT_ERROR.ADMISSION_FAILED, cause); error.message = ['IMPORT_VERIFICATION_INVALID','IMPORT_SESSION_CHANGED'].includes(cause.code) ? `${stage}: ${cause.message} Selected files are retained in this tab.` : studioRequestError(cause, stage, 'Selected files are retained. Retry this same import after restoring access.').message; throw error; }
  if(admit&&!intake){
    const batch=await admitWorkspaceBatch({rows:[{rootFile,companions,clientItemId:'board'}],projectId,accountScope:scope,api,isCurrent:()=>scope===currentWorkspaceAccountScope()});
    const item=batch.items.find(row=>row.clientItemId==='board');if(item?.state!=='admitted'||!item.itemId)throw new WorkspaceImportError(IMPORT_ERROR.ADMISSION_FAILED);
    intake={id:item.itemId,companions:companions.map(file=>({name:file.name,sizeBytes:file.size}))};
  }

  let asset;
  stage = 'Upload initialization';
  const progress = (pct, phase) => { stage = phase.includes('complet') ? 'Upload completion' : phase.includes('register') ? 'Package registration' : phase.includes('sign') ? 'Upload part authorization' : phase.includes('upload') ? 'Source transfer' : phase === 'done' ? 'Source verification' : 'Upload initialization'; onProgress?.(pct, phase); };
  try {
    asset = companions.length
      ? await uploadPackage(rootFile, companions, projectId, api, progress, ...(intake ? [intake] : []))
      : await uploadFile(rootFile, projectId, api, progress, signal, ...(intake ? [undefined, intake] : []));
  } catch (err) {
    const failure = new WorkspaceImportError(err.code === 'PACKAGE_INCOMPLETE' ? IMPORT_ERROR.PACKAGE_INCOMPLETE : IMPORT_ERROR.UPLOAD_FAILED, err);
    if (err.code !== 'PACKAGE_INCOMPLETE') failure.message = studioRequestError(err, stage, 'Retry this same item to reconcile stored bytes; do not start a second import.').message;
    throw failure;
  }

  // A missing Asset id means there is nothing to hand off - never invent one.
  if (!asset?._id) throw new WorkspaceImportError(IMPORT_ERROR.UPLOAD_FAILED);

  // The transport resolving is not the same as the import succeeding: the
  // backend can complete an upload and still return a failed or provisional
  // Asset, which must never be handed off as success.
  if (String(asset.projectId) !== String(projectId) || String(asset.userId) !== userId || !isHandoffableAsset(asset, fileInfo) || asset.metadata?.packageImport?.status === 'incomplete' || asset.metadata?.packageRegistrationFailed) {
    throw new WorkspaceImportError(IMPORT_ERROR.INVALID_UPLOAD_RESULT);
  }

  if (intake) {
    onProgress?.(100, 'registering project source');
    let attached;
    try { attached = await attachImportedSource(asset, api); }
    catch (error) { throw studioRequestError(error, 'Project source attachment', 'The source is stored. Retry this same item to attach it without uploading again.'); }
    if (String(attached?._id) !== String(asset._id) || String(attached?.userId) !== userId || String(attached?.projectId) !== String(projectId)) throw new WorkspaceImportError(IMPORT_ERROR.INVALID_UPLOAD_RESULT);
    asset = attached;
    if(scope===currentWorkspaceAccountScope()&&asset.workspaceRevision)onRevisionSaved?.(asset.workspaceRevision);
  }
  return {
    asset,
    packageInfo: { isPackage: companions.length > 0, companionCount: companions.length },
    fileInfo,
  };
}
